import React, { useState, useEffect } from "react";
import { IAddTestSeries } from "../../TestSeries.props";
import { useForm } from "react-hook-form";
import { CancelButton, FormButton } from "../../../../components/Botton";
import Loader from "../../../../components/Loader";
import useAddTestSeries from "./useAddTestSeries";
import QuestionsModal from "../QuestionSelectionModal/QuestionsModal";
import { Switch, FormControlLabel } from "@mui/material";

const AddTestSeries: React.FC = () => {
  const {
    formData,
    loading,
    questionsModal,
    handleChange,
    handleConfigChange,
    handleAddConfigField,
    handleRemoveConfigField,
    handleSubmit,
    handleCancel,
    handleCustomQuestionsConfirm,
    handleModalClose,
    fetchQuestionsForConfig,
    languages,
    courses,
    subjectName,
    standard,
    handlePageChange,
    handleSearchChange  // Add this from the hook
  } = useAddTestSeries();

  const {
    register,
    handleSubmit: useFormSubmit,
    formState: { errors },
  } = useForm<IAddTestSeries>();

  const [selectedStandard, setSelectedStandard] = useState<string | undefined>(undefined);
  const [selectedCourse, setSelectedCourse] = useState<string | undefined>(undefined);
  const [filteredCourses, setFilteredCourses] = useState(courses || []);
  const [filteredSubjects, setFilteredSubjects] = useState(subjectName || []);

  useEffect(() => {
    if (selectedStandard) {
      const filtered = courses?.filter((course) => course.standard === selectedStandard);
      setFilteredCourses(filtered || []);
      setSelectedCourse(undefined); // Reset course when standard changes
    } else {
      setFilteredCourses([]);
    }
  }, [selectedStandard, courses]);

  // Effect for filtering subjects based on standard and course
  useEffect(() => {
    if (selectedStandard && selectedCourse) {
      const filtered = subjectName?.filter(
        (subject) =>
          subject.standard === selectedStandard &&
          subject.courseName === selectedCourse
      );
      setFilteredSubjects(filtered || []);
    } else {
      setFilteredSubjects([]);
    }
  }, [selectedStandard, selectedCourse, subjectName]);

  const fields = [
    {
      label: "Test Series Name",
      name: "testSeriesName",
      type: "text",
    },
    {
      label: "Education Language",
      name: "languageName",
      type: "select",
      options: languages || [],
    },
    {
      label: "Education",
      name: "standard",
      type: "select",
      options: standard || [],
    },
    {
      label: "Stream",
      name: "courseName",
      type: "select",
      options: filteredCourses,
    },
    {
      label: "Duration of Test",
      name: "duration",
      type: "number",
    },
    {
      label: "Amount",
      name: "amount",
      type: "number",
    },
  ] as const;

  return (
    <div className="flex flex-col items-center w-full h-screen p-8">
      <div className="bg-white shadow-md rounded-lg pt-6 pb-6 w-full">
        <div className="border-b-2 w-[100%]">
          <h1 className="text-3xl mb-2 text-gray-700 ml-5">Add TestSeries</h1>
        </div>
        {loading && <Loader />}
        <form onSubmit={useFormSubmit(handleSubmit)} className="space-y-6">
          <fieldset className="m-2 rounded border-opacity-60 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 m-4">
              {fields.map((field) => (
                <div key={field.name}>
                  <label
                    className="block text-gray-700 text-base font-semibold mb-2"
                    htmlFor={field.name}
                  >
                    {field.label}:
                  </label>
                  {field.type === "select" ? (
                    <select
                      id={field.name}
                      {...register(field.name, { required: true })}
                      onChange={(e) => {
                        handleChange(e);
                        if (field.name === "standard") {
                          setSelectedStandard(e.target.value);
                        } else if (field.name === "courseName") {
                          setSelectedCourse(e.target.value);
                        }
                      }}
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                      disabled={field.name === "courseName" && !selectedStandard}
                    >
                      <option value="">Select {field.label}</option>
                      {field.options.map((option) => (
                        <option key={option._id} value={option[field.name]}>
                          {option[field.name]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                      type={field.type}
                      id={field.name}
                      {...register(field.name, {
                        required: true,
                        ...(field.name === "duration" && {
                          validate: (value) => Number(value) > 0 || "Duration must be greater than 0",
                        }),
                        ...(field.name === "amount" && {
                          validate: (value) => Number(value) >= 0 || "Amount must be >= 0",
                        }),
                      })}
                      value={formData[field.name]}
                      onChange={handleChange}
                    />
                  )}
                  {errors[field.name] && (
                    <span className="text-red-500 text-xs italic">
                      {errors[field.name]?.message || `${field.label} field is required`}
                    </span>
                  )}

                </div>
              ))}

              {/* Premium/Free Toggle Switch */}
              <div className="col-span-2">
                <label className="block text-gray-700 text-base font-semibold mb-3">
                  Test Series Type:
                </label>
                <div className="flex items-center gap-4">
                  <span className="inline-flex items-center px-4 py-1.5 rounded-md text-white font-bold text-sm"
                    style={{
                      background: formData.isPaid
                        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                        : "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                      boxShadow: formData.isPaid
                        ? "0 2px 8px rgba(102, 126, 234, 0.3)"
                        : "0 2px 8px rgba(17, 153, 142, 0.3)",
                      minWidth: "95px",
                      textAlign: "center",
                    }}
                  >
                    {formData.isPaid ? "⭐ Premium" : "Free"}
                  </span>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isPaid}
                        onChange={(e) => handleChange({ target: { id: 'isPaid', value: e.target.checked } } as any)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#667eea',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#667eea',
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-sm text-gray-600 ml-1">
                        {formData.isPaid ? 'Switch to Free' : 'Switch to Premium'}
                      </span>
                    }
                  />
                </div>
              </div>
            </div>
          </fieldset>
          <fieldset className="m-2 ml-6 rounded border-opacity-60 bg-white">
            <legend className="text-xl mb-4 font-semibold text-gray-700">Configurations</legend>
            {formData.config.map((config, index) => (
              <div key={index} className="border p-4 rounded mb-4">
                <h3 className="text-gray-700 font-medium mb-2">Config {index + 1}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-700 text-base font-semibold mb-2">
                      Subject:
                    </label>
                    <select
                      value={config.subject}
                      onChange={(e) => handleConfigChange(e, index, "subject")}
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                      disabled={!selectedStandard || !selectedCourse}
                    >
                      <option value="">Select Subject</option>
                      {filteredSubjects.map((subject) => (
                        <option key={subject._id} value={subject.subject}>
                          {subject.subject}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-base font-semibold mb-2">
                      Level:
                    </label>
                    <select
                      value={config.level}
                      onChange={(e) => handleConfigChange(e, index, "level")}
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                    >
                      <option value="">Select Level</option>
                      <option value="Easy">Easy</option>
                      <option value="Medium">Medium</option>
                      <option value="Hard">Hard</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-base font-semibold mb-2">
                      Marks:
                    </label>
                    <input
                      type="number"
                      value={config.marks}
                      onChange={(e) => handleConfigChange(e, index, "marks")}
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                    />
                  </div>
                </div>

                {/* Custom Questions Selection Section */}
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => fetchQuestionsForConfig(config.subject, config.level, config.marks, index)}
                    disabled={!config.subject || !config.level || !config.marks  || !formData.courseName}
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Select Custom Questions
                  </button>

                  {config.random === false && config.questions && config.questions.length > 0 && (
                    <span className="ml-3 text-sm text-green-600 font-medium">
                      ✓ Custom Selected ({config.questions.length} questions)
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => handleRemoveConfigField(index)}
                  className="mt-4 text-red-500 hover:underline"
                >
                  Remove Config
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddConfigField}
              className="text-white hover:underline p-2 rounded-full bg-blue-500"
            >
              Add Field
            </button>
          </fieldset>
          <div className="flex justify-start mt-6 ml-10">
            <FormButton
              className="py-2 px-4 rounded mr-4"
              type="submit"
              children="ADD"
            />
            <CancelButton
              className="py-2 px-4 rounded"
              onClick={handleCancel}
              children="Cancel"
              type="button"
            />
          </div>
        </form>
      </div>

      <QuestionsModal
        isOpen={questionsModal.isOpen}
        onClose={handleModalClose}
        questions={questionsModal.questions}
        alreadySelectedQuestions={questionsModal.selectedQuestions}
        maxSelection={questionsModal.marks}
        onConfirm={handleCustomQuestionsConfirm}
        subject={questionsModal.subject}
        level={questionsModal.level}
        // Pagination props
        totalPages={questionsModal.totalPages}
        currentPage={questionsModal.currentPage}
        totalDocs={questionsModal.totalDocs}
        hasNext={questionsModal.hasNext}
        hasPrev={questionsModal.hasPrev}
        onPageChange={handlePageChange}
        loading={questionsModal.loading}
        // Add search handler prop
        onSearchChange={handleSearchChange}
      />
    </div>
  );
};

export default AddTestSeries;